'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { removeCentsFromAmount } from '@/utils/currency';
import { TypeProduct as ContentfulProduct } from '@/core/generated-types';
import {
  TypeProduct,
  TypeSubscriberShopProduct,
  TypeVariant,
} from '@/lib/api/productCatalog/types';
import {
  ShopifyProductType,
  ShopifyVariant,
  getProductInventoryStatus,
} from '@/lib/shopify';
import { captureTypeProductEvent, pushEvent } from '@/utils/dataLayer';
import classNames from 'classnames';
import { Badge, Button } from '@/components/ui';
import { addToCart, removeFromCart } from '@/lib/cart.browser';
import { TypeResponseError } from '@/lib/api/config';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations';
import { mutate } from 'swr';
import {
  getShopifyProductVariant,
  getTranslationForRule,
  isOrderFrequencyFreezePeriodActive,
} from '../SubscriberShop/utils';
import { getAssetForProduct } from '@/lib/api/productCatalog/helpers';
import moment from 'moment';
import { VariantModal } from '../VariantModal';
import { PRODUCT_EVENT_TYPES } from '@/utils/rudderstack';
import { usePathname } from '@/navigation';
import SiblingsAddToCartCta from './SiblingsAddToCartCta';
import { ProductContextProvider } from '@/context/ProductContext';
import { InventoryStatusCopy } from '@/lib/contentful';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { LookInsideModal } from '../LookInsideModal';
import useCartContext from '@/context/CartContext';

interface SiblingsProductCardProps {
  subscriberShopProduct: TypeSubscriberShopProduct;
  addedProducts: TypeSubscriberShopProduct[];
  handleAddedProducts: (addedProducts: TypeSubscriberShopProduct[]) => void;
  openLifetimeMaxModal?: () => void;
  translations: TypeSubscriberShopPageTranslations;
  inventoryStatusCopy: InventoryStatusCopy | null;
  openErrorModal?: () => void;
  itemOrigin: string;
}

const SiblingsProductCard = ({
  subscriberShopProduct,
  addedProducts,
  handleAddedProducts,
  openLifetimeMaxModal,
  translations,
  inventoryStatusCopy,
  openErrorModal,
  itemOrigin,
}: SiblingsProductCardProps) => {
  const pathname = usePathname();
  const { cart } = useCartContext();
  const productVariantIds = new Set(
    subscriberShopProduct.shopifyProduct?.variants.map(
      (variant) => variant.id,
    ) || [],
  );
  const isProductAddedToCart = cart?.lines?.edges.some(({ node }) =>
    productVariantIds.has(node.merchandise.id),
  );
  const [isLoading, setIsLoading] = useState(false);
  const {
    product,
    shopifyProduct,
    purchase_metadata: purchaseMetadata,
    rule_tags: ruleTags,
    kit_metadata: kitMetadata,
    contentfulProduct,
  } = subscriberShopProduct;
  const variants = shopifyProduct?.variants || [];
  const defaultVariant: ShopifyVariant = shopifyProduct
    ? getShopifyProductVariant(
        kitMetadata,
        product.variants as TypeVariant[],
        variants as ShopifyVariant[],
      )
    : ({} as ShopifyVariant);
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const { formattedPrice } = defaultVariant;
  const { name, display_age_range: displayAgeRange } = product;
  const asset = getAssetForProduct(product);
  const quantity = 1;
  const isPlayKits = product.product_type === ShopifyProductType.PLAY_KITS;
  const isWithinOrderFreezePeriod = isOrderFrequencyFreezePeriodActive(
    purchaseMetadata,
    moment().format('YYYY-MM-DD'),
  );
  const isVariantSelectionRequired = variants.length > 1;
  const inventoryStatus = getProductInventoryStatus(shopifyProduct);
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] =
    useState(false);

  if (isWithinOrderFreezePeriod) return null;

  const handleAddProductToCart = async () => {
    try {
      setIsLoading(true);
      const response = await addToCart(
        [
          {
            merchandiseId: selectedVariant?.id,
            quantity: quantity,
            attributes: [
              {
                key: 'source',
                value: 'sub_shop',
              },
              {
                key: 'item_origin',
                value: itemOrigin,
              },
              {
                key: 'quantity_limit',
                value: purchaseMetadata?.order_max
                  ? String(purchaseMetadata.order_max)
                  : 'unlimited',
              },
              {
                key: 'quantity_limited_to_one',
                value: String(isPlayKits || purchaseMetadata?.order_max === 1),
              },
            ],
          },
        ],
        quantity,
        { noredirect: 1 },
      );

      if (response.ok) {
        const { purchased_count: purchasedCount, lifetime_max: lifetimeMax } =
          purchaseMetadata;
        if (purchasedCount >= lifetimeMax) {
          openLifetimeMaxModal && openLifetimeMaxModal();
          return;
        }
        handleAddedProducts([...addedProducts, subscriberShopProduct]);
        mutate('/cart');
        pushEvent('addToCart', {
          qtyVal: quantity,
          shopifyProduct,
          variant: selectedVariant,
        });
        captureTypeProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_ADDED, {
          shopifyProduct,
          variant: selectedVariant,
          product: product,
          quantity,
          url: pathname || '',
        });
      } else {
        console.error(
          'Failed to add product to cart:',
          (response as TypeResponseError).errorMessage,
        );
        openErrorModal && openErrorModal();
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveProductFromCart = async () => {
    try {
      setIsLoading(true);
      const response = await removeFromCart([selectedVariant?.id], {
        noredirect: 1,
      });

      if (response.ok) {
        const filteredProducts = addedProducts.filter(
          (addedProduct) =>
            addedProduct.product.external_id !== product.external_id,
        );
        handleAddedProducts(filteredProducts);
        mutate('/cart');
        pushEvent('removeFromCart', {
          qtyVal: quantity,
          shopifyProduct,
          variant: selectedVariant,
        });
        captureTypeProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_REMOVED, {
          shopifyProduct,
          variant: selectedVariant,
          product: product,
          quantity,
          url: pathname || '',
        });
      } else {
        console.error(
          'Failed to remove product from cart:',
          (response as TypeResponseError).errorMessage,
        );
      }
    } catch (error) {
      console.error('Error removing product from cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const allowedRule = ruleTags.find((tag) =>
    getTranslationForRule(tag, translations),
  );

  return (
    <div
      data-testid="sub-shop-product-card"
      className={classNames(
        `mb-4 flex flex-col rounded-md border-[3px] p-5 shadow-md md:mb-0`,
        isProductAddedToCart ? 'border-indigo' : 'border-transparent',
      )}
    >
      <ProductContextProvider
        value={{
          enableYotpoReviewsSummary: null,
          enableTrustedShopsReviewsSummary: null,
          product: contentfulProduct as ContentfulProduct,
          shopifyProduct,
          internalProduct: product as TypeProduct,
          inventoryStatusCopy,
        }}
      >
        <div className="flex-auto">
          {asset ? (
            <div className="relative bg-khaki-tint">
              {allowedRule ? (
                <div className="absolute left-3 top-3">
                  <Badge
                    badgeColor={allowedRule === 'refill' ? 'Pink' : 'Indigo'}
                    text={
                      getTranslationForRule(allowedRule, translations) ||
                      allowedRule
                    }
                  />
                </div>
              ) : null}
              <Image
                src={asset.uri}
                alt={asset.alt_text || `${name} image`}
                height={240}
                width={320}
                className="mx-auto h-[240px] object-contain"
              />
            </div>
          ) : null}
          <div className="mt-3 flex">
            <div className="mr-auto">
              {contentfulProduct ? (
                <>
                  <Button
                    ctaText={contentfulProduct.fields.title}
                    onClick={() =>
                      isVariantSelectionRequired
                        ? setIsVariantModalOpen(true)
                        : setIsProductDetailsModalOpen(true)
                    }
                    size={BUTTON_SIZES.md}
                    style={BUTTON_STYLES.link}
                    classes="px-0 text-lg min-h-fit h-auto font-bold text-left"
                  />
                  <p>{contentfulProduct.fields.ageRange}</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-bold">{name}</p>
                  {displayAgeRange && <p>{displayAgeRange}</p>}
                </>
              )}
            </div>
            {formattedPrice && (
              <p className="ml-2 text-lg font-bold">
                {removeCentsFromAmount(formattedPrice)}
              </p>
            )}
          </div>
        </div>
        <div className="mt-2 border-t pt-4">
          <SiblingsAddToCartCta
            isProductAddedToCart={isProductAddedToCart}
            isLoading={isLoading}
            handleRemoveProductFromCart={handleRemoveProductFromCart}
            handleAddProductToCart={
              isVariantSelectionRequired
                ? () => setIsVariantModalOpen(true)
                : handleAddProductToCart
            }
            translations={translations}
            inventoryStatus={inventoryStatus}
            shopifyProduct={shopifyProduct}
          />
        </div>

        {isVariantSelectionRequired && (
          <VariantModal
            isOpen={isVariantModalOpen}
            handleClose={() => setIsVariantModalOpen(false)}
            addToCart={handleAddProductToCart}
            asset={asset}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            translations={translations}
            isProductAddedToCart={isProductAddedToCart}
          />
        )}

        {contentfulProduct && (
          <LookInsideModal
            isOpen={isProductDetailsModalOpen}
            handleClose={() => setIsProductDetailsModalOpen(false)}
            product={contentfulProduct}
            addProduct={handleAddProductToCart}
            removeProduct={handleRemoveProductFromCart}
            isSelected={isProductAddedToCart}
            shopifyProduct={shopifyProduct}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            internalProduct={product}
          />
        )}
      </ProductContextProvider>
    </div>
  );
};

export default SiblingsProductCard;
